import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { AnyClip, AspectRatio, ClipSelection, ProjectAsset, Timeline as TimelineType, VideoClip } from '../../types/editor';
import { isIOS } from './editorState';
import { TEXT_FONT_FAMILIES, applyDynamics, framingFor } from './migrateTimeline';

const DRIFT_THRESHOLD = 0.1; // seconds — only re-seek when out of sync by this much

function clipDur(c: AnyClip): number { return c.trimOut - c.trimIn; }

function findActiveClips(clips: VideoClip[], t: number): VideoClip[] {
  const active: VideoClip[] = [];
  for (let i = 0; i < clips.length; i++) {
    const c = clips[i];
    const start = c.start;
    const end = c.start + clipDur(c);
    if (t >= start && t < end) {
      active.push(c);
      continue;
    }
    // Outgoing-clip extension: when the NEXT clip's transitionIn is
    // crossfade, keep this clip "alive" during the transition window
    // so the canvas has something to fade FROM (otherwise we'd be
    // crossfading from canvas-bg, producing the fade-to-black bug).
    if (i + 1 < clips.length) {
      const next = clips[i + 1];
      const xfade = next.transitionIn;
      if (xfade?.type === 'crossfade' && xfade.duration > 0) {
        const transEnd = next.start + xfade.duration;
        if (t >= end && t < transEnd) active.push(c);
      }
    }
  }
  return active;
}

interface FitRect { dx: number; dy: number; dw: number; dh: number; }

function containFit(srcW: number, srcH: number, dstW: number, dstH: number): FitRect {
  if (!srcW || !srcH) return { dx: 0, dy: 0, dw: dstW, dh: dstH };
  const scale = Math.min(dstW / srcW, dstH / srcH);
  const dw = srcW * scale;
  const dh = srcH * scale;
  return { dx: (dstW - dw) / 2, dy: (dstH - dh) / 2, dw, dh };
}

type MediaElement = HTMLVideoElement | HTMLImageElement;

interface PreviewCanvasProps {
  timeline: TimelineType;
  /** Which aspect's framing overrides drive the canvas. Drives the
   * canvas dimensions (via timeline.project.{width,height}) and the
   * per-clip zoom/pan applied during drawImage. */
  aspect: AspectRatio;
  assets: ProjectAsset[];
  playing: boolean;
  playhead: number;
  onPlayheadUpdate: (t: number) => void;
  onPlayingChange: (playing: boolean) => void;
  /** Clicking the canvas selects the clip visible at the playhead —
   * same selection the timeline blocks drive (opens the inspector). */
  onSelectClip?: (selection: ClipSelection | null) => void;
}

export default function PreviewCanvas({
  timeline, aspect, assets, playing, playhead, onPlayheadUpdate, onPlayingChange,
  onSelectClip,
}: PreviewCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoElsRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const imageElsRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const rafRef = useRef<number | null>(null);
  const wallStartRef = useRef(0);
  const tStartRef = useRef(0);
  const unlockedRef = useRef(false);
  const isIOSDevice = useMemo(() => isIOS(), []);

  // Warm the overlay fonts so the first canvas draw doesn't render a
  // fallback face (canvas doesn't trigger lazy @font-face loads).
  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) return;
    for (const fam of ['OverlayImpact', 'OverlayBangers', 'OverlayMarker', 'OverlayLobster']) {
      document.fonts.load(`700 24px '${fam}'`).catch(() => { /* offline dev */ });
    }
  }, []);

  const assetById = useMemo(() => {
    const m = new Map<number, ProjectAsset>();
    for (const a of assets || []) m.set(a.id, a);
    return m;
  }, [assets]);

  const W = timeline.project.width;
  const H = timeline.project.height;
  const totalDuration = timeline.project.duration || 0;

  const videoClips = timeline.video.clips.filter((c) => assetById.get(c.assetId)?.kind === 'video');
  const imageClips = timeline.video.clips.filter((c) => assetById.get(c.assetId)?.kind === 'image');
  const audioClips = timeline.audio.clips;

  const renderFrame = useCallback((t: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = timeline.project.bgColor || '#000';
    ctx.fillRect(0, 0, W, H);

    const active = findActiveClips(timeline.video.clips, t);
    for (const clip of active) {
      const asset = assetById.get(clip.assetId);
      if (!asset) continue;
      // Clamp source time to trimOut so an extended outgoing clip
      // freezes on its last in-trim frame during the transition
      // rather than wandering past it.
      const srcT = Math.min(clip.trimIn + (t - clip.start), clip.trimOut);

      let source: MediaElement | null = null;
      if (asset.kind === 'video') {
        const v = videoElsRef.current.get(clip.id);
        if (v) {
          if (Math.abs(v.currentTime - srcT) > DRIFT_THRESHOLD) {
            try { v.currentTime = srcT; } catch (_) { /* ignored */ }
          }
          source = v;
        }
      } else if (asset.kind === 'image') {
        source = imageElsRef.current.get(clip.id) || null;
      }
      if (!source) continue;

      let alpha = 1;
      const xfade = clip.transitionIn;
      if (xfade && xfade.type === 'crossfade' && xfade.duration > 0) {
        const into = t - clip.start;
        if (into < xfade.duration) {
          alpha = isIOSDevice ? 1 : into / xfade.duration;
        }
      }
      ctx.globalAlpha = alpha;

      const v = source as HTMLVideoElement & HTMLImageElement;
      // Prefer the live element's dimensions: the browser reports them
      // rotation-corrected, while probed asset.width/height can be the
      // coded (pre-rotation) size on older rows — using those stretches
      // portrait phone footage into a landscape fit rect.
      const sw = v.videoWidth || v.naturalWidth || asset.width || W;
      const sh = v.videoHeight || v.naturalHeight || asset.height || H;
      const fit = containFit(sw, sh, W, H);
      // Apply per-aspect framing on top of contain-fit, then overlay
      // any aspect-independent dynamics (pan/zoom) interpolated over
      // the clip's progress 0..1. Mirrors backend render math.
      const baseFraming = framingFor(timeline, aspect, clip.id);
      const dur = clip.trimOut - clip.trimIn;
      const progress = dur > 0 ? (t - clip.start) / dur : 0;
      const framing = applyDynamics(baseFraming, clip.dynamics, progress);
      const dw = fit.dw * framing.scale;
      const dh = fit.dh * framing.scale;
      const dx = (W - dw) / 2 + framing.offsetX * W / 2;
      const dy = (H - dh) / 2 + framing.offsetY * H / 2;
      try {
        ctx.drawImage(source, dx, dy, dw, dh);
      } catch (_) { /* element not yet ready */ }
    }
    ctx.globalAlpha = 1;

    /* Text overlays — drawn above the video, mirroring the backend's
       drawtext: base size = fontSize × canvas height × per-aspect
       zoom; offsets place the block as canvas fractions from center;
       dark outline keeps it readable on any footage. */
    for (const tclip of timeline.overlay.clips) {
      if (t < tclip.start || t >= tclip.start + (tclip.trimOut - tclip.trimIn)) continue;
      const text = (tclip.text || '').trim();
      if (!text) continue;
      const framing = framingFor(timeline, aspect, tclip.id);
      const px = Math.max(8, tclip.fontSize * H * framing.scale);
      const cx = W / 2 + framing.offsetX * W / 2;
      const cy = H / 2 + framing.offsetY * H / 2;
      const family = TEXT_FONT_FAMILIES[tclip.font || 'sans'] || TEXT_FONT_FAMILIES.sans;
      ctx.font = `700 ${px}px ${family}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      const lines = text.split('\n');
      const lineH = px * 1.2;
      const firstY = cy - ((lines.length - 1) * lineH) / 2;
      // Optional background box behind the whole block, mirroring
      // drawtext's box= (padding ≈ fontsize/3).
      if (tclip.bgColor) {
        const pad = px / 3;
        let maxW = 0;
        for (const ln of lines) maxW = Math.max(maxW, ctx.measureText(ln).width);
        const bx = cx - maxW / 2 - pad;
        const by = firstY - px / 2 - pad;
        const bw = maxW + pad * 2;
        const bh = (lines.length - 1) * lineH + px + pad * 2;
        const radius = Math.min((tclip.bgRadius ?? 0) * px, bh / 2);
        ctx.fillStyle = tclip.bgColor;
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') ctx.roundRect(bx, by, bw, bh, radius);
        else ctx.rect(bx, by, bw, bh);
        ctx.fill();
      }
      // Glyph-only vertical nudge — the background box stays put.
      const offY = (tclip.textOffsetY ?? 0) * px;
      ctx.lineWidth = Math.max(2, px * 0.12);
      ctx.strokeStyle = tclip.outlineColor || '#000000';
      ctx.fillStyle = tclip.color || '#ffffff';
      for (let li = 0; li < lines.length; li += 1) {
        const y = firstY + li * lineH + offY;
        ctx.strokeText(lines[li], cx, y);
        ctx.fillText(lines[li], cx, y);
      }
    }
  }, [timeline, aspect, assetById, W, H, isIOSDevice]);

  const driveAudio = useCallback((t: number, isPlaying: boolean) => {
    for (const clip of audioClips) {
      const el = audioElsRef.current.get(clip.id);
      if (!el) continue;
      const inRange = t >= clip.start && t < clip.start + clipDur(clip);
      if (inRange) {
        const srcT = clip.trimIn + (t - clip.start);
        if (Math.abs(el.currentTime - srcT) > DRIFT_THRESHOLD * 1.5) {
          try { el.currentTime = srcT; } catch (_) { /* ignored */ }
        }
        if (el.volume !== clip.volume) el.volume = Math.max(0, Math.min(1, clip.volume));
        if (isPlaying && el.paused) { el.play().catch(() => {}); }
        if (!isPlaying && !el.paused) el.pause();
      } else if (!el.paused) {
        el.pause();
      }
    }
    const active = findActiveClips(timeline.video.clips, t).filter((c) => assetById.get(c.assetId)?.kind === 'video');
    const activeIds = new Set(active.map((c) => c.id));
    // Original-soundtrack gain per video clip (clip.volume, default 1).
    // The element caps at 1.0 — boosts above 100% only apply in the
    // final render's mix.
    const volById = new Map<string, number>();
    for (const c of timeline.video.clips) volById.set(c.id, (c as VideoClip).volume ?? 1);
    videoElsRef.current.forEach((v, id) => {
      const vol = Math.max(0, Math.min(1, volById.get(id) ?? 1));
      if (v.muted !== (vol <= 0.001)) v.muted = vol <= 0.001;
      if (Math.abs(v.volume - vol) > 0.01) v.volume = vol;
      if (activeIds.has(id) && isPlaying) {
        if (v.paused) v.play().catch(() => {});
      } else if (!v.paused) {
        v.pause();
      }
    });
  }, [audioClips, timeline.video.clips, assetById]);

  useEffect(() => {
    if (!playing) {
      renderFrame(playhead);
      driveAudio(playhead, false);
    }
  }, [playing, playhead, renderFrame, driveAudio]);

  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    wallStartRef.current = performance.now();
    tStartRef.current = playhead;
    const tick = (): void => {
      const t = tStartRef.current + (performance.now() - wallStartRef.current) / 1000;
      if (totalDuration > 0 && t >= totalDuration) {
        renderFrame(totalDuration);
        driveAudio(totalDuration, false);
        onPlayheadUpdate(totalDuration);
        onPlayingChange(false);
        return;
      }
      renderFrame(t);
      driveAudio(t, true);
      onPlayheadUpdate(t);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  /* Click-to-inspect: the clip under the playhead is what the user is
     looking at, so select it (topmost = drawn last when a crossfade
     keeps two alive). Empty canvas click clears the selection. */
  const handleCanvasClick = useCallback(() => {
    if (!onSelectClip) return;
    const active = findActiveClips(timeline.video.clips, playhead);
    const top = active[active.length - 1];
    onSelectClip(top ? { track: 'video', id: top.id } : null);
  }, [onSelectClip, timeline.video.clips, playhead]);

  const handleFirstPlay = useCallback(() => {
    if (unlockedRef.current) return;
    unlockedRef.current = true;
    const unlock = (el: HTMLVideoElement | HTMLAudioElement): void => {
      try {
        const p = el.play();
        if (p && typeof p.then === 'function') p.then(() => el.pause()).catch(() => {});
        else el.pause();
      } catch (_) { /* ignored */ }
    };
    videoElsRef.current.forEach(unlock);
    audioElsRef.current.forEach(unlock);
  }, []);

  return (
    <div className="preview">
      <div className="preview-frame" style={{ aspectRatio: `${W} / ${H}` }}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="preview-canvas"
          onPointerDown={handleFirstPlay}
          onClick={handleCanvasClick}
        />
        {isIOSDevice && timeline.video.clips.some((c) => c.transitionIn?.type === 'crossfade') && (
          <div className="preview-ios-note">Preview shows hard cuts; final render will crossfade.</div>
        )}
      </div>
      <div className="preview-hidden" aria-hidden>
        {videoClips.map((clip) => (
          <video
            key={clip.id}
            ref={(el) => {
              if (el) videoElsRef.current.set(clip.id, el);
              else videoElsRef.current.delete(clip.id);
            }}
            src={assetById.get(clip.assetId)?.file_url || undefined}
            /* not `muted` — driveAudio sets per-clip volume/mute so the
               original soundtrack mixes into preview playback; the
               first-play unlock routine still runs inside a gesture. */
            playsInline
            preload="auto"
          />
        ))}
        {imageClips.map((clip) => (
          <img
            key={clip.id}
            ref={(el) => {
              if (el) imageElsRef.current.set(clip.id, el);
              else imageElsRef.current.delete(clip.id);
            }}
            src={assetById.get(clip.assetId)?.file_url || ''}
            alt=""
          />
        ))}
        {audioClips.map((clip) => (
          <audio
            key={clip.id}
            ref={(el) => {
              if (el) audioElsRef.current.set(clip.id, el);
              else audioElsRef.current.delete(clip.id);
            }}
            src={assetById.get(clip.assetId)?.file_url || undefined}
            preload="auto"
          />
        ))}
      </div>
    </div>
  );
}
